import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FeatureFlags } from '../../../../components/Authorizer';
import InsuredCell from '../../../../components/search/SearchTable/cells/InsuredCell';
import {
  reportsToggleCheckAll, reportsToggleChecked,
  selectReportsCheckAllChecked,
  selectReportsIsRowChecked
} from '../../../../redux/modules/reports';
import { createHeaderCell } from '../../../../utils/tableUtil';

import CheckboxCell from './cells/CheckboxCell';
import DocumentCell from './cells/DocumentCell';
import ExternalIdCell from './cells/ExternalIdCell';
import ItemsCell from './cells/ItemsCell';
import JobInfoCell from './cells/JobInfoCell';
import LatestEmailCell from './cells/LatestEmailCell';
import ParentIdCell from './cells/ParentIdCell';
import PolicyCell from './cells/PolicyCell';
import RequirementsCell from './cells/RequirementsCell';
import RequirementStatusCell from './cells/RequirementStatusCell';

export const ColumnTypes = {
  Checkbox: 'Checkbox',
  Insured: 'Insured',
  ExternalId: 'Insured External Ids',
  InsuredId: 'Insured Id',
  ParentId: 'Parent Id',
  JobInfo: 'Project Info',
  Requirements: 'Min. Required Coverage',
  ExpiredRequirements: 'Expired Requirements',
  Items: 'Units And Codes',
  Policy: 'Policies',
  Documents: 'Documents',
  EmailSent: 'Email Sent',
  OverallStatus: 'Overall Status',
  LatestEmail: 'Last Email'
};

const columns = [
  {
    header: ({ data }) => {
      const dispatch = useDispatch();
      const checked = useSelector(selectReportsCheckAllChecked);
      return (
        <CheckboxCell
          borderColor="greyScale.400"
          isChecked={checked}
          onChange={e => dispatch(reportsToggleCheckAll(e.target.checked, data))}
        />
      );
    },
    cell: ({ value, row: { original } }) => {
      const dispatch = useDispatch();
      const checkAllChecked = useSelector(selectReportsCheckAllChecked);
      const rowChecked = useSelector(selectReportsIsRowChecked(value));
      return (
        <CheckboxCell
          isChecked={!!(checkAllChecked || rowChecked)}
          onChange={e => dispatch(reportsToggleChecked({
            insuredId: value,
            jobId: original.job.id,
            recordId: original.id,
            insuredName: original.insured.companyName,
            insuredIntegrations: original?.insuredIntegrations ?? [],
            insuredExternalId: original.insured?.externalId
          }, e.target.checked))}
        />
      );
    },
    accessor: 'insured.id',
    disableSortBy: true,
    colSpan: 1,
    justifyContent: 'flex-start',
    disableToggle: true,
    name: ColumnTypes.Checkbox,
    csv_accessor: 'insured_checkbox'
  },
  {
    header: createHeaderCell(ColumnTypes.Insured, 'flex-start'),
    cell: InsuredCell,
    accessor: 'insured',
    headerClassName: 'ReportContent__center',
    sortKey: 'insured.company_name.keyword',
    sortName: 'companyName',
    justifyContent: 'flex-start',
    colSpan: 1,
    name: ColumnTypes.Insured,
    csv_accessor: 'insured_name'
  },
  {
    header: createHeaderCell(ColumnTypes.ExternalId, 'flex-start'),
    id: 'insured.external_id',
    cell: ExternalIdCell,
    accessor: 'insured',
    headerClassName: 'ReportContent__center',
    sortKey: 'insured.external_id.keyword',
    sortName: 'externalId',
    justifyContent: 'flex-start',
    colSpan: 1,
    name: ColumnTypes.ExternalId,
    csv_accessor: 'insured_external_id'
  },
  {
    header: createHeaderCell(ColumnTypes.ParentId, 'flex-start'),
    id: 'parentInsured.id',
    cell: ParentIdCell,
    accessor: 'parentInsured',
    justifyContent: 'flex-start',
    colSpan: 1,
    disableSortBy: true,
    name: ColumnTypes.ParentId,
    csv_accessor: 'parent_insured_external_id'
  },
  {
    header: createHeaderCell(ColumnTypes.JobInfo),
    id: 'job.id',
    cell: JobInfoCell,
    accessor: 'insuranceRequirements',
    sortKey: 'job.name.keyword',
    sortName: 'jobName',
    colSpan: 1,
    name: ColumnTypes.JobInfo,
    csv_accessor: 'job_name'
  },
  {
    id: 'requirementStatus',
    header: createHeaderCell(ColumnTypes.OverallStatus),
    cell: RequirementStatusCell,
    accessor: 'insured.overallStatus',
    disableSortBy: true,
    colSpan: 1,
    name: ColumnTypes.OverallStatus,
    csv_accessor: 'insured_overall_status'
  },
  {
    id: 'item',
    header: createHeaderCell(ColumnTypes.Items),
    cell: ItemsCell,
    accessor: 'insuranceRequirements',
    colSpan: 1,
    disableSortBy: true,
    name: ColumnTypes.Items,
    csv_accessor: 'items_and_codes',
    flag: FeatureFlags.Items
  },
  {
    header: createHeaderCell(ColumnTypes.Requirements),
    cell: RequirementsCell,
    accessor: 'insuranceRequirements',
    colSpan: 1,
    disableSortBy: true,
    name: ColumnTypes.Requirements,
    csv_accessor: 'minimum_required_coverage'
  },
  {
    id: 'policies',
    header: createHeaderCell(ColumnTypes.Policy),
    cell: PolicyCell,
    accessor: 'insuranceRequirements',
    colSpan: 1,
    disableSortBy: true,
    name: ColumnTypes.Policy,
    csv_accessor: 'policies'
  },
  {
    id: 'documents',
    header: createHeaderCell(ColumnTypes.Documents),
    cell: DocumentCell,
    accessor: 'insuranceRequirements',
    colSpan: 1,
    disableSortBy: true,
    name: ColumnTypes.Documents,
    csv_accessor: 'documents',
    flag: FeatureFlags.SearchByDocuments
  },
  {
    header: createHeaderCell(ColumnTypes.LatestEmail),
    cell: LatestEmailCell,
    accessor: 'insured.latestEmail',
    sortKey: 'insured.latest_email.id',
    colSpan: 1,
    disableSortBy: false,
    name: ColumnTypes.LatestEmail,
    csv_accessor: 'last_email_sent'
  }
];

export default columns;
